import { gql } from "@apollo/client";

export const PHOTO_DATA = gql`
  fragment PhotoData on Photo {
    url
    thumbnailUrl
    approved
    verified
    uploadId
    uploadComplete
    isPrivate
    uploadById
    platform
    moderation {
      checkedDate
      faceCount
      genderDetected
      genderConfidence
      ageRangeLow
      ageRangeHigh
      moderationLabels
      celebrityName
      celebrityConfidence
      graphicsLabels
      outcome
      rejectReason
      reviewReasons
    }
  }
`;

export const VIDEO_DATA = gql`
  fragment VideoData on Video {
    _id
    url
    thumbnailUrl
    thumbnailBlurhash
    approved
    verified
    uploadId
    uploadComplete
    isPrivate
    uploadById
  }
`;

export const PAGING_DATA = gql`
  fragment PagingData on PagingResult {
    total
    limit
    page
    totalPages
    offset
    hasPrevPage
    hasNextPage
    prevPage
    nextPage
    pagingCounter
  }
`;

export const HELLO = gql`
  query hello {
    hello
  }
`;

export const DASHBOARD = gql`
  query dashboard {
    dashboard {
      totalUsers
      totalGroups
      totalInvites
      totalUnverifiedPhotos
      totalUnresolvedReports
      totalBannedUsers
      totalUnverifiedUsers
      totalPrivatePhotos
      dailyActiveUsers
      totalUsersWithApprovedPhoto
    }
  }
`;

export const USER = gql`
  ${PHOTO_DATA}
  query user($_id: String!) {
    user(_id: $_id) {
      _id
      username
      email
      firstName
      lastName
      describeYourself
      deleted
      premium
      admin
      credits
      created
      lastActive
      loginDate
      banned
      bannedUntilDate
      translationTokens
      profilePhoto {
        ...PhotoData
      }
      profilePhotoApproved
      photos {
        ...PhotoData
      }
      platform
      country
      gender
      liveBan
      liveStreamingApproved
      liveStreamingApprovedDate
      liveStreamingApprovedBy
      liveStreamingApplication {
        ticketId
        ticketNumber
        date
        status
        decidedDate
        decidedBy
        reason
        retryAfterDate
      }
    }
  }
`;

export const TRANSLATE_PROFILE = gql`
  query translateProfile($userId: String!) {
    translateProfile(userId: $userId) {
      firstName
      lastName
      describeYourself
    }
  }
`;

export const USER_DIAMOND_STATUS = gql`
  query userDiamondStatus($userId: String!) {
    userDiamondStatus(userId: $userId) {
      currentDiamonds
      totalEarned
      totalPaid
      currentDiamondHistory {
        date
        diamondsEarned
        type
        giftType
        qualifiedViews
        reelId
        reelThumbnailUrl
      }
    }
  }
`;

export const UNVERIFIED_PHOTOS = gql`
  ${PAGING_DATA}
  ${PHOTO_DATA}
  query unverifiedPhotos($args: PublicPhotosArgs!) {
    unverifiedPhotos(args: $args) {
      paging {
        ...PagingData
      }
      nodes {
        hasPhotoHash
        ...PhotoData
      }
    }
  }
`;

export const UNVERIFIED_VIDEOS = gql`
  ${PAGING_DATA}
  ${VIDEO_DATA}
  query unverifiedVideos($paging: PagingArgs!) {
    unverifiedVideos(paging: $paging) {
      paging {
        ...PagingData
      }
      nodes {
        ...VideoData
      }
    }
  }
`;

export const PRIVATE_PHOTOS = gql`
  ${PAGING_DATA}
  ${PHOTO_DATA}
  query privatePhotos($paging: PagingArgs!) {
    privatePhotos(paging: $paging) {
      paging {
        ...PagingData
      }
      nodes {
        ...PhotoData
      }
    }
  }
`;

export const PRIVATE_VIDEOS = gql`
  ${PAGING_DATA}
  ${VIDEO_DATA}
  query privateVideos($paging: PagingArgs!) {
    privateVideos(paging: $paging) {
      paging {
        ...PagingData
      }
      nodes {
        ...VideoData
      }
    }
  }
`;

export const USERS = gql`
  ${PAGING_DATA}
  query users($regex: String, $paging: PagingArgs!) {
    users(regex: $regex, paging: $paging) {
      paging {
        ...PagingData
      }
      nodes {
        _id
        username
        email
        deleted
        premium
        admin
        credits
        lastActive
        loginDate
        banned
        bannedUntilDate
        translationTokens
      }
    }
  }
`;

export const PROFILE_PHOTOS_APPROVAL = gql`
  ${PHOTO_DATA}
  query profilePhotosApproval($args: ProfilePhotosApprovalArgs!) {
    profilePhotosApproval(args: $args) {
      _id
      username
      platform
      country
      gender
      profilePhoto {
        hasPhotoHash
        ...PhotoData
      }
      pushAvailable
      appVersion
      premium
      storeFront
      profilePhotoAutoApprovedNeedReview
      liveBan
    }
  }
`;

export const MATCH_VIDEOS_APPROVAL = gql`
  ${PHOTO_DATA}
  query matchVideosApproval {
    matchVideosApproval {
      _id
      username
      profilePhoto {
        ...PhotoData
      }
      matchVideo {
        url
        approved
        verified
        uploadId
        uploadComplete
        isPrivate
        thumbnailUrl
        thumbnailBlurhash
      }
    }
  }
`;

export const REELS_APPROVAL = gql`
  query reelsApproval($args: ReelsApprovalArgs!) {
    reelsApproval(args: $args) {
      reels {
        _id
        url
        thumbnailUrl
        thumbnailBlurhash
        uploadId
        uploadComplete
        byUserId
        description
        profilePhotoUrl
        tags
        matchVideo
        callToAction
        callToActionType
        callToActionButtonColor
        callToActionButtonDelayMs
        hlsReady
        hlsError
        explicit
      }
      searchedReels {
        _id
        url
        thumbnailUrl
        thumbnailBlurhash
        uploadId
        uploadComplete
        byUserId
        description
        profilePhotoUrl
        tags
        matchVideo
        hlsReady
        explicit
      }
    }
  }
`;

export const USER_APPROVED_REELS = gql`
  query userApprovedReels($args: ReelsApprovalArgs!) {
    reelsApproval(args: $args) {
      searchedReels {
        _id
        byUserId
        url
        thumbnailUrl
        uploadId
        uploadComplete
        views
        description
        tags
        matchVideo
        hlsReady
        explicit
      }
    }
  }
`;

export const UNRESOLVED_REPORTS = gql`
  query unresolvedReports {
    unresolvedReports {
      _id
      byUserId
      type
      problem
      message
      contentId
      resolved
      resolvedDate
      resolvedByEmail
      created
    }
  }
`;

export const NEWS = gql`
  query news($limit: Int!, $lang: String) {
    news(limit: $limit, lang: $lang) {
      _id
      title
      subtitle
      message
      date
    }
  }
`;

export const PHOTO_VERIFICATIONS = gql`
  ${PHOTO_DATA}
  query photoVerifications {
    photoVerifications {
      userId
      profilePhoto {
        ...PhotoData
      }
      verifyPhoto {
        ...PhotoData
      }
    }
  }
`;

export const PUBLIC_POSTS_PENDING_REVIEW = gql`
  query publicPostsPendingReview {
    publicPostsPendingReview {
      _id
      byUserId
      audience
      created
      text
      reviewStatus
      attachments {
        url
        thumbnailUrl
        uploadComplete
        uploadId
      }
    }
  }
`;

export const ANALYTICS = gql`
  query analytics {
    analytics {
      totalVerifiedUsers
      totalFemaleUsers
      totalMaleUsers
      totalOtherGenderUsers
      totalUsersWithPremium
      totalPayingUsers
      totalActiveUsersLast30days
      totalPayingUsersLast30days
      totalActiveUsersLast30daysCreatedMoreThan90daysAgo
      totalNewUsersLast30days
      daus {
        date
        users
        mau
      } # Daily active users for last 60 days. One item per day.
      totalUsers {
        date
        users
      } # Total users for last year. One item per month.
      totalUsersPlatformIos
      totalUsersPlatformAndroid
      totalUsersPlatformWeb
      totalUsersLoginApple
      totalUsersLoginFacebook
      totalUsersLoginGoogle
      totalUsersLoginEmail
      totalUsersByCountry {
        country
        users
      }
    }
  }
`;

export const GROUPS = gql`
  ${PHOTO_DATA}
  query groups {
    groups {
      _id
      created
      privacy
      visibility
      memberVisibility
      memberPostsAllowed
      memberCommentsAllowed
      name
      about
      rules
      genderLimited
      minAgeLimited
      maxAgeLimited
      photo {
        ...PhotoData
      }
    }
  }
`;

export const GROUP_PHOTOS = gql`
  ${PHOTO_DATA}
  query groupPhotos {
    groupPhotos {
      _id
      photo {
        ...PhotoData
      }
    }
  }
`;

export const SUBSCRIPTIONS = gql`
  query subscriptions($args: SubscriptionsArgs!) {
    subscriptions(args: $args) {
      totalSubscriptions
      totalTrials
      data {
        userId
        expireDate
        age
        country
        trial
      }
      analyticData {
        sources {
          source
          count
        }
        adAttributions {
          adGroup
          keyword
          country
          trial
        }
        countries {
          country
          count
        }
      }
    }
  }
`;

export const USER_ACTIVITY_MAP = gql`
  ${PHOTO_DATA}
  query userActivityMap($args: UserActivityMapArgs!) {
    userActivityMap(args: $args) {
      data {
        lastActive
        userId
        username
        lat
        lng
        photo {
          ...PhotoData
        }
        country
        city
        created
        birthDate
        currentAppVersion
        premium
        vip
        appPlatform
      }
    }
  }
`;

export const STOREFRONT_MAP = gql`
  ${PHOTO_DATA}
  query storefrontMap($args: UserActivityMapArgs!) {
    userActivityMap(args: $args) {
      data {
        lastActive
        userId
        username
        lat
        lng
        photo {
          ...PhotoData
        }
        country
        city
        created
        birthDate
        currentAppVersion
        premium
        vip
        appPlatform
        storeFront
      }
    }
  }
`;

export const DELETED_USERS = gql`
  query deletedUsers {
    deletedUsers {
      _id
      username
      deletedReason
      deletedText
      lastActive
    }
  }
`;

export const SURVEYS = gql`
  query surveys {
    surveys {
      _id
      title
      totalAnswers
    }
  }
`;

export const SURVEY = gql`
  query GetSurvey($id: String!) {
    survey(_id: $id) {
      _id
      title
      description
      totalAnswers
      questions {
        text
        type
        choices {
          label
        }
      }
    }
  }
`;

export const SURVEY_ANALYTICS = gql`
  query GetSurveyAnalytics($id: String!) {
    surveyAnalytics(_id: $id) {
      _id
      title
      description
      totalAnswers
      questionAnalytics {
        questionIndex
        text
        type
        totalAnswers
        choiceAnalytics {
          label
          count
          percentage
        }
      }
    }
  }
`;

export const SURVEY_TEXT_ANSWERS = gql`
  query GetSurveyTextAnswers($id: String!, $questionIndex: Int!) {
    surveyTextAnswers(_id: $id, questionIndex: $questionIndex) {
      _id
      userId
      answer
      createdAt
    }
  }
`;

export const SIGNUP_FLOW = gql`
  query signupFlow($args: SignupFlowArgs!) {
    signupFlow(args: $args) {
      totalUsers
      stepOnboarding1
      stepOnboarding1Active
      stepOnboarding2
      stepOnboarding2Active
      stepOnboarding3
      stepOnboarding3Active
      stepOnboardingPaywall
      stepOnboardingPaywallActive
      stepCompleted
      premiumPurchased
    }
  }
`;

export const ONBOARDING_STATS = gql`
  query onboardingStats($args: OnboardingArgs!) {
    onboardingStats(args: $args) {
      totalUsers
      totalPushActivated
      totalDistribution {
        totalUsers
        maleUsers
        femaleUsers
        otherGenderUsers
      }
      atMatchingPreferences {
        totalUsers
        maleUsers
        femaleUsers
        otherGenderUsers
      }
      activeAtMatchingPreferences
      atMatch {
        totalUsers
        maleUsers
        femaleUsers
        otherGenderUsers
      }
      activeAtMatch
      atReady {
        totalUsers
        maleUsers
        femaleUsers
        otherGenderUsers
      }
      activeAtReady
      atAuth {
        totalUsers
        maleUsers
        femaleUsers
        otherGenderUsers
      }
      activeAtAuth
      finishedOnboarding {
        totalUsers
        maleUsers
        femaleUsers
        otherGenderUsers
      }
      finishedOnboardingWithPhoto
      finishedOnboardingWithPhotoMale
      finishedOnboardingWithPhotoFemale
      finishedOnboardingWithLocation
      totalRewardedAdsWatched
      premiumTotal
      premiumOnboarding
      premiumWithoutFlag
    }
  }
`;

export const ONBOARDING_HISTORY = gql`
  query onboardingHistory($args: OnboardingHistoryArgs!) {
    onboardingHistory(args: $args) {
      onboardingData {
        date
        platform
        appVersions {
          version
          count
        }
        totalUsers
        totalPremiumUsers
        totalIapUsers
        newPremium1Week
        renewalPremium1Week
        newPremium3Months
        renewalPremium3Months
        totalHotLikesIap
        totalWinksIap
        totalUnlockChatIap
        totalBoostIap
        totalSpotlightIap
        totalVotesIap
        totalLiveGiftIap
        atPreferences
        atMatch
        atReady
        atAuth
        totalFinishedOnboarding
        femaleFinishedOnboarding
        photoFinishedOnboarding
        pushFinishedOnboarding
        totalPremiumOnboarding
        totalPushActivated
        totalRewardedAdsWatched
        inviteCodesUsed
        totalActiveInviteCodes
        userCount
        subscriptionSources {
          source
          count
        }
      }
    }
  }
`;

export const PAID_CONVERSION = gql`
  query paidConversion($args: PaidConversionArgs!) {
    paidConversion(args: $args) {
      totalSubscriptions
      totalUsersCreated
      totalOnboardingFinished
      purchasesFromExistingUsers
      sources {
        source
        count
      }
    }
  }
`;

export const VOICE_RECORDINGS = gql`
  ${PHOTO_DATA}
  query voiceRecordings {
    voiceRecordings {
      _id
      username
      profilePhoto {
        ...PhotoData
      }
      voicePrompts {
        created
        text
        uploadComplete
        uploadId
        url
      }
    }
  }
`;

export const VIDEO_CHAT_STATUS = gql`
  query videoChatStatus {
    videoChatStatus {
      activeSearchersCount
      activeSessionsCount
      activeSessions {
        user1 {
          _id
          username
          photoThumbnailUrl
        }
        user2 {
          _id
          username
          photoThumbnailUrl
        }
        sessionDurationSeconds
      }
    }
  }
`;

export const ACTIVE_LIVE_STREAMS = gql`
  query getActiveLiveStreams($limit: Int!) {
    getActiveLiveStreams(limit: $limit) {
      nodes {
        _id
        title
        description
        status
        isPrivate
        allowComments
        allowGifts
        currentViewerCount
        peakViewerCount
        totalDiamondsEarned
        thumbnailUrl
        startedAt
        createdAt
        hostUser {
          id
          name
          profilePhotoThumbnailUrl
          profilePhotoUrl
        }
      }
    }
  }
`;

export const GET_LIVE_STREAM = gql`
  query getLiveStream($streamId: String!) {
    getLiveStream(streamId: $streamId) {
      _id
      title
      description
      status
      isPrivate
      allowComments
      allowGifts
      currentViewerCount
      peakViewerCount
      totalDiamondsEarned
      thumbnailUrl
      startedAt
      endedAt
      createdAt
      hostUser {
        id
        name
        profilePhotoThumbnailUrl
        profilePhotoUrl
      }
    }
  }
`;

export const REEL_COMMENTS = gql`
  ${PAGING_DATA}
  query reelComments($paging: PagingArgs!) {
    reelComments(paging: $paging) {
      paging {
        ...PagingData
      }
      nodes {
        _id
        reelId
        reelThumbnailUrl
        byUserId
        byUsername
        byUserProfilePhotoUrl
        text
        created
      }
    }
  }
`;

export const REEL_WITH_COMMENTS = gql`
  query reelWithComments($reelId: String!) {
    reelWithComments(reelId: $reelId) {
      reel {
        _id
        url
        thumbnailUrl
        thumbnailBlurhash
        uploadId
        byUserId
        description
        profilePhotoUrl
        tags
      }
      comments {
        _id
        reelId
        byUserId
        byUsername
        byUserProfilePhotoUrl
        text
        created
      }
    }
  }
`;

export const VIDEO_CHAT_HISTORY = gql`
  query videoChatHistory($limit: Int, $offset: Int) {
    videoChatHistory(limit: $limit, offset: $offset) {
      totalCount
      sessions {
        _id
        sessionId
        type
        status
        sessionStartedAt
        sessionEndedAt
        durationSeconds
        endReason
        endedBy
        participant1 {
          _id
          username
          photoThumbnailUrl
        }
        participant1JoinedAt
        participant1LeftAt
        participant2 {
          _id
          username
          photoThumbnailUrl
        }
        participant2JoinedAt
        participant2LeftAt
        messages {
          id
          fromUserId
          text
          timestamp
        }
        connectionQuality {
          averageLatency
          packetsLost
          reconnections
        }
        totalMessageCount
        chatId
        createdAt
      }
    }
  }
`;

export const SUPPORT_TICKET_DATA = gql`
  fragment SupportTicketData on SupportTicket {
    _id
    number
    displayId
    status
    source
    channel
    senderEmail
    senderName
    subject
    userId
    user {
      _id
      username
      premium
      banned
      deleted
      country
    }
    language
    category
    firstInboundAt
    lastInboundAt
    lastOutboundAt
    messageCount
    hasAttachments
    spamScore
    spamReasons
    blockedSender
    snoozedUntil
    needsAdmin
    report {
      type
      problem
      contentId
      reportedUserId
      reportedUsername
    }
  }
`;

export const SUPPORT_TICKET_THREAD_DATA = gql`
  fragment SupportTicketThreadData on SupportTicket {
    ...SupportTicketData
    autoReplyCount
    resolution {
      by
      at
      note
    }
    notes {
      at
      by
      text
    }
    messages {
      _id
      direction
      receivedAt
      fromName
      text
      fullText
      attachments {
        name
        size
        mime
      }
      sentBy
      via
      historical
      autoSubmitted
      bounced
      inAppStatus
    }
  }
  ${SUPPORT_TICKET_DATA}
`;

export const SUPPORT_TICKETS = gql`
  query supportTickets(
    $filter: SupportTicketFilterInput
    $limit: Int!
    $offset: Int
  ) {
    supportTickets(filter: $filter, limit: $limit, offset: $offset) {
      total
      limit
      offset
      hasNextPage
      nodes {
        ...SupportTicketData
        preview
        previewDirection
      }
    }
  }
  ${SUPPORT_TICKET_DATA}
`;

export const SUPPORT_TICKET = gql`
  query supportTicket($_id: String!) {
    supportTicket(_id: $_id) {
      ...SupportTicketThreadData
    }
  }
  ${SUPPORT_TICKET_THREAD_DATA}
`;

export const SUPPORT_TICKET_COUNTS = gql`
  query supportTicketCounts {
    supportTicketCounts {
      open
      snoozed
      waitingUser
      resolved
      spam
      archived
      needsAdmin
    }
  }
`;

export const TRANSLATE_SUPPORT_TICKET = gql`
  query translateSupportTicket($_id: String!) {
    translateSupportTicket(_id: $_id) {
      subject
      messages {
        messageId
        text
      }
    }
  }
`;
